/**
 * Deadline fire: flames creep up from the bottom of the window until they lick
 * a third of the way up, then a fire extinguisher slides in from the bottom
 * right and sweeps foam across them until they're out.
 */
import { calm, rand, scene } from "./scene";

/** The beats, in ms from the start. */
const GROW_MS = 2500;
const EXT_IN_AT = 2400;
const EXT_SLIDE_MS = 650;
const SPRAY_AT = 3100;
const SPRAY_MS = 2500;
/** Late enough that the last lobs have landed on the far edge and the fire's out. */
const EXT_OUT_AT = 6200;
/** By now the last foam has landed, and whatever fire it missed goes out anyway, so it always ends. */
const MOP_UP_AT = SPRAY_AT + SPRAY_MS + 900;
const FOAM_FADE_AT = 6900;
const FOAM_FADE_MS = 1400;
/** Stop by now whatever's left. */
const END_MS = 9500;

/** The fire is a row of columns along the bottom, each with its own heat and its own share of foam. */
const COL_W = 24;
const MAX_COLS = 400;
/** Past this many columns, the same number of flames spread thinner (and bigger) rather than drawing more. */
const CROWD_COLS = 70;

/** Flames per column per 60 Hz frame at full heat. */
const FLAME_RATE = 0.2;
/** How far rising flames sway, in px per frame at the tip. */
const LICK = 0.8;
/** Doused flames burn through their colours this many times faster, so the fire dies where the foam lands, not a second later. */
const DOUSED_BURN = 3;
/** Of the flames that burn out, how many leave a puff of smoke; doused ones nearly always do. */
const SMOKE_CHANCE = 0.25;
const SMOKE_ALPHA = 0.32;
/** Per column at the bottom edge; neighbours overlap to about three times this. */
const GLOW_ALPHA = 0.14;

/** Foam blobs per 60 Hz frame while spraying, per 900 px of width. */
const FOAM_RATE = 1.1;
/** Foam's fall, in px per frame² per px of window height, so lobs keep their shape at any size. */
const GRAVITY = 0.00036;
/** How much one blob puts out where it lands, and how far either side. */
const DOUSE_HIT = 0.5;
const DOUSE_R = 60;
const DOUSE_MAX = 2;
/** Foam creeps along the floor, so fire just past where it lands (behind the extinguisher, say) goes out too. */
const SPREAD_RATE = 0.12;
const SPREAD_KEEP = 0.93;
const MOP_UP_RATE = 0.035;
/** How far one landed blob raises the pile under it, as a share of its radius. */
const PILE_GAIN = 0.5;
/** The nozzle wobbles this many times as it sweeps left, never quite turning back, which would pile foam in mounds where it turned. */
const WOBBLES = 3;
const WOBBLE_AMP = 0.015;
/** How wide the spray fans, as a share of the whole sweep either side of where it's aimed. */
const FAN = 0.07;
/** The furthest the foam is aimed, in from the left edge so its spread still lands on screen. */
const FAR_X = 40;

/** Sprites are rendered at this size and scaled. */
const SPRITE = 96;
const STAGES = 14;
/** White-hot at the base, through yellow, orange and red to embers, as a flame rises. */
const FLAME_KEYS_DARK = [
  [255, 250, 228],
  [255, 226, 120],
  [255, 168, 42],
  [255, 108, 22],
  [222, 54, 20],
  [146, 28, 14],
  [86, 20, 14],
];
/** White-hot would vanish on a white page, so this starts at yellow, with too little blue to add up to white. */
const FLAME_KEYS_LIGHT = [
  [255, 206, 64],
  [255, 170, 36],
  [250, 124, 20],
  [236, 82, 16],
  [200, 42, 14],
  [140, 24, 10],
  [90, 18, 10],
];

/** Where the hose meets the nozzle, in the extinguisher's own units (about 100 tall, origin at the bottom middle). */
const NOZZLE_X = -30;
const NOZZLE_Y = -78;
const NOZZLE_LEN = 15;
const INK = "#171717";

const FLAME = 0;
const SMOKE = 1;

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (x: number) => {
  const k = clamp01(x);
  return k * k * (3 - 2 * k);
};
const easeOut = (x: number) => 1 - (1 - clamp01(x)) ** 3;
const easeIn = (x: number) => clamp01(x) ** 3;

function sprite(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = c.height = SPRITE;
  return [c, c.getContext("2d")!];
}

/** A soft round blob of one colour; `stops` are [radius, alpha] pairs. */
function blob([r, g, b]: number[], stops: number[][]) {
  const [c, ctx] = sprite();
  const half = SPRITE / 2;
  const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
  for (const [at, a] of stops) grad.addColorStop(at, `rgba(${r}, ${g}, ${b}, ${a})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SPRITE, SPRITE);
  return c;
}

/** One blob per step of a flame's life, so a frame only picks a sprite rather than building a gradient. */
function flameSprites(keys: number[][]) {
  const out: HTMLCanvasElement[] = [];
  for (let k = 0; k < STAGES; k++) {
    const at = (k / (STAGES - 1)) * (keys.length - 1);
    const i = Math.min(keys.length - 2, Math.floor(at));
    const rgb = keys[i].map((v, j) => Math.round(v + (keys[i + 1][j] - v) * (at - i)));
    out.push(blob(rgb, [[0, 1], [0.3, 0.8], [0.65, 0.28], [1, 0]]));
  }
  return out;
}

/** Shaded foam bubbles, outlined so they still show on a white page. */
function foamSprite(cluster: boolean) {
  const [c, ctx] = sprite();
  const s = SPRITE / 2;
  ctx.strokeStyle = "rgba(90, 110, 132, 0.55)";
  ctx.lineWidth = 3.5;
  const bubble = (x: number, y: number, r: number) => {
    const grad = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r);
    grad.addColorStop(0, "#ffffff");
    grad.addColorStop(0.6, "#f2f5f8");
    grad.addColorStop(1, "#c5d0db");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  };
  if (cluster) {
    bubble(s, s * 0.75, s * 0.52);
    bubble(s * 0.72, s * 1.18, s * 0.48);
    bubble(s * 1.3, s * 1.22, s * 0.44);
  } else bubble(s, s, s - 2);
  return c;
}

/** Whether the page is dark, so smoke and the black fittings get a shade that shows against it. */
function darkPage() {
  const m = getComputedStyle(document.body).backgroundColor.match(/[\d.]+/g);
  if (!m || m[3] === "0") return false;
  return +m[0] * 0.3 + +m[1] * 0.59 + +m[2] * 0.11 < 110;
}

/** The cylinder with its domed top. */
function cylinder() {
  const p = new Path2D();
  p.moveTo(-17, -3);
  p.lineTo(-17, -70);
  p.ellipse(0, -70, 17, 15, 0, Math.PI, Math.PI * 2);
  p.lineTo(17, -3);
  p.quadraticCurveTo(17, 0, 14, 0);
  p.lineTo(-14, 0);
  p.quadraticCurveTo(-17, 0, -17, -3);
  p.closePath();
  return p;
}

interface Paints {
  body: CanvasGradient;
  label: CanvasGradient;
}

/** Gradients in the extinguisher's own units, so they hold at any size and position. */
function paints(ctx: CanvasRenderingContext2D): Paints {
  const body = ctx.createLinearGradient(-17, 0, 17, 0);
  body.addColorStop(0, "#8a140e");
  body.addColorStop(0.2, "#d42e1f");
  body.addColorStop(0.33, "#f25b45");
  body.addColorStop(0.45, "#df3526");
  body.addColorStop(0.8, "#b01f17");
  body.addColorStop(1, "#73110c");
  const label = ctx.createLinearGradient(-17, 0, 17, 0);
  label.addColorStop(0, "#cbc2ad");
  label.addColorStop(0.32, "#fbf7ec");
  label.addColorStop(0.45, "#ffffff");
  label.addColorStop(1, "#c3b9a3");
  return { body, label };
}

/** Strokes the current path in black, over a pale rim on a dark page so it doesn't disappear. */
function blackLine(ctx: CanvasRenderingContext2D, width: number, px: number, rim: string | null) {
  if (rim) {
    ctx.strokeStyle = rim;
    ctx.lineWidth = width + 2.4 * px;
    ctx.stroke();
  }
  ctx.strokeStyle = INK;
  ctx.lineWidth = width;
  ctx.stroke();
}

/** Fills the current path in black, rimmed on a dark page. */
function blackFill(ctx: CanvasRenderingContext2D, px: number, rim: string | null) {
  if (rim) {
    ctx.strokeStyle = rim;
    ctx.lineWidth = 2.4 * px;
    ctx.stroke();
  }
  ctx.fillStyle = INK;
  ctx.fill();
}

/** The extinguisher in its own units, origin at the bottom middle. `aim` points the nozzle; `px` is one screen pixel. */
function drawExtinguisher(ctx: CanvasRenderingContext2D, paint: Paints, body: Path2D, aim: number, px: number, rim: string | null) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.fillStyle = "rgba(0, 0, 0, 0.16)";
  ctx.beginPath();
  ctx.ellipse(0, 0, 24, 3.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // The carry handle and the squeeze lever, both behind the valve head.
  ctx.beginPath();
  ctx.moveTo(3, -93);
  ctx.lineTo(29, -90.5);
  ctx.moveTo(-5, -99);
  ctx.lineTo(28, -105);
  blackLine(ctx, 4, px, rim);

  ctx.fillStyle = paint.body;
  ctx.fill(body);
  ctx.fillStyle = paint.label;
  ctx.fillRect(-17, -54, 34, 28);
  ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
  ctx.fillRect(-17, -54, 34, 0.8);
  ctx.fillRect(-17, -26.8, 34, 0.8);
  ctx.fillStyle = "#c22a1d";
  ctx.fillRect(-10, -50, 20, 4.5);
  ctx.fillStyle = "#7a7163";
  ctx.fillRect(-10, -43, 17, 1.6);
  ctx.fillRect(-10, -39.5, 20, 1.6);
  ctx.fillRect(-10, -36, 13, 1.6);
  ctx.fillRect(-10, -32, 4, 3);
  ctx.fillRect(-4.5, -32, 4, 3);
  ctx.fillRect(1, -32, 4, 3);
  ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
  ctx.beginPath();
  ctx.roundRect(-12, -79, 3, 74, 1.5);
  ctx.fill();
  ctx.strokeStyle = "#5a0d09";
  ctx.lineWidth = 1.4 * px;
  ctx.stroke(body);

  ctx.fillStyle = "#222222";
  ctx.beginPath();
  ctx.roundRect(-17.6, -5, 35.2, 5, 2);
  ctx.fill();

  // The hose leaves the valve head and comes into the nozzle from behind, whichever way it points.
  const dx = Math.cos(aim);
  const dy = Math.sin(aim);
  ctx.beginPath();
  ctx.moveTo(-8, -95);
  ctx.bezierCurveTo(-14, -84, NOZZLE_X - dx * 11, NOZZLE_Y - dy * 11, NOZZLE_X, NOZZLE_Y);
  blackLine(ctx, 4.2, px, rim);

  ctx.save();
  ctx.translate(NOZZLE_X, NOZZLE_Y);
  ctx.rotate(aim);
  ctx.beginPath();
  ctx.moveTo(-1, -2.6);
  ctx.lineTo(NOZZLE_LEN, -3.8);
  ctx.lineTo(NOZZLE_LEN, 3.8);
  ctx.lineTo(-1, 2.6);
  ctx.closePath();
  blackFill(ctx, px, rim);
  ctx.fillStyle = "#5a5a5a";
  ctx.fillRect(-1.5, -3.2, 3, 6.4);
  ctx.fillRect(NOZZLE_LEN - 1.5, -3.8, 1.5, 7.6);
  ctx.restore();

  // The steel neck, the valve head over the end of the hose, and its pressure gauge.
  ctx.fillStyle = "#7c7c7c";
  ctx.fillRect(-6, -91, 12, 8);
  ctx.beginPath();
  ctx.roundRect(-9, -99, 18, 9, 2);
  blackFill(ctx, px, rim);
  ctx.fillStyle = "#f4f1e8";
  ctx.strokeStyle = "#555555";
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.arc(-1.5, -94.5, 3.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = "#c22a1d";
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-1.5, -94.5);
  ctx.lineTo(0.4, -96.3);
  ctx.stroke();
}

export default function play(): Promise<void> {
  const quiet = calm();
  const dark = darkPage();
  const maxFlames = quiet ? 220 : 700;
  const maxFoam = quiet ? 120 : 360;
  const flameRate = quiet ? FLAME_RATE * 0.4 : FLAME_RATE;
  const foamRate = quiet ? FOAM_RATE * 0.35 : FOAM_RATE;
  const wobbleAmp = quiet ? 0 : WOBBLE_AMP;
  /** How far up the window the flames lick. */
  const reach = quiet ? 0.2 : 0.33;
  /** How fast the flames flicker and sway. */
  const pace = quiet ? 0.5 : 1;
  const flameAlpha = dark ? 0.8 : 0.62;
  const rim = dark ? "rgba(225, 225, 225, 0.4)" : null;

  const flameArt = flameSprites(dark ? FLAME_KEYS_DARK : FLAME_KEYS_LIGHT);
  const glowArt = blob([255, 118, 28], [[0, 1], [0.45, 0.5], [1, 0]]);
  const smokeArt = blob(dark ? [140, 132, 126] : [72, 64, 60], [[0, 0.85], [0.5, 0.45], [1, 0]]);
  const foamArt = [foamSprite(false), foamSprite(true)];
  const body = cylinder();
  let paint: Paints | null = null;

  // Particles live in flat typed arrays, so a frame allocates nothing.
  const fX = new Float32Array(maxFlames);
  const fY = new Float32Array(maxFlames);
  const fVx = new Float32Array(maxFlames);
  const fVy = new Float32Array(maxFlames);
  const fAge = new Float32Array(maxFlames);
  const fLife = new Float32Array(maxFlames);
  const fR = new Float32Array(maxFlames);
  const fSeed = new Float32Array(maxFlames);
  const fKind = new Uint8Array(maxFlames);
  let flameCount = 0;

  const bX = new Float32Array(maxFoam);
  const bY = new Float32Array(maxFoam);
  const bVx = new Float32Array(maxFoam);
  const bVy = new Float32Array(maxFoam);
  const bR = new Float32Array(maxFoam);
  const bAge = new Float32Array(maxFoam);
  const bLanded = new Uint8Array(maxFoam);
  const bArt = new Uint8Array(maxFoam);
  let foamCount = 0;
  let foamCarry = 0;
  /** Steps through the fan by the golden ratio, so blobs fill its gaps evenly rather than clumping at random. */
  let fanStep = Math.random();

  /** How much foam each column has had, 1 being out; double-buffered so it creeps evenly both ways. */
  let douse = new Float32Array(MAX_COLS);
  let next = new Float32Array(MAX_COLS);
  const heat = new Float32Array(MAX_COLS);
  /** Fractional flames owed to the next frame, so the fire is even at any frame rate. */
  const carry = new Float32Array(MAX_COLS);
  /** How high the foam has piled in each column. */
  const pile = new Float32Array(MAX_COLS);
  const phaseA = rand(0, Math.PI * 2);
  const phaseB = rand(0, Math.PI * 2);

  let cols = 1;
  let wide = 1;
  let floor = 0;
  let gravity = 0;
  let svx = 0;
  let svy = 0;

  const colOf = (x: number) => Math.max(0, Math.min(cols - 1, Math.floor(x / COL_W)));

  /**
   * The launch velocity (into svx, svy) that lobs foam from (x, y) onto the pile at `toX`: higher and slower the further it
   * goes, and about a second across the whole window whatever its size, so the far edge is out in time on a big screen.
   */
  const solve = (x: number, y: number, toX: number) => {
    const dx = toX - x;
    const dy = floor - pile[colOf(toX)] - 4 - y;
    const time = 20 + (48 * Math.abs(dx)) / wide;
    svx = dx / time;
    svy = (dy - 0.5 * gravity * time * time) / time;
  };

  const douseAt = (x: number) => {
    for (let j = colOf(x - DOUSE_R), end = colOf(x + DOUSE_R); j <= end; j++) {
      const d = Math.abs((j + 0.5) * COL_W - x);
      if (d < DOUSE_R) douse[j] = Math.min(DOUSE_MAX, douse[j] + DOUSE_HIT * (1 - d / DOUSE_R));
    }
  };

  /** Removes a flame by moving the last one into its slot. */
  const dropFlame = (i: number) => {
    const last = --flameCount;
    fX[i] = fX[last];
    fY[i] = fY[last];
    fVx[i] = fVx[last];
    fVy[i] = fVy[last];
    fAge[i] = fAge[last];
    fLife[i] = fLife[last];
    fR[i] = fR[last];
    fSeed[i] = fSeed[last];
    fKind[i] = fKind[last];
  };

  const dropFoam = (i: number) => {
    const last = --foamCount;
    bX[i] = bX[last];
    bY[i] = bY[last];
    bVx[i] = bVx[last];
    bVy[i] = bVy[last];
    bR[i] = bR[last];
    bAge[i] = bAge[last];
    bLanded[i] = bLanded[last];
    bArt[i] = bArt[last];
  };

  return scene(({ ctx, w, h, t, dt }) => {
    cols = Math.min(MAX_COLS, Math.ceil(w / COL_W) + 1);
    wide = w;
    floor = h;
    gravity = GRAVITY * h;
    const tall = h * reach;
    const crowd = Math.min(1, CROWD_COLS / cols);
    const size = Math.max(0.6, Math.min(1.5, tall / 300)) / Math.sqrt(crowd);
    const intensity = clamp01(t / GROW_MS);

    // Foam creeps along the floor; once the spray's done, whatever it missed goes out anyway.
    const creep = Math.min(1, SPREAD_RATE * dt);
    const mop = t > MOP_UP_AT ? MOP_UP_RATE * dt : 0;
    for (let i = 0; i < cols; i++) {
      const d = douse[i];
      const want = Math.max(i > 0 ? douse[i - 1] : 0, i < cols - 1 ? douse[i + 1] : 0) * SPREAD_KEEP;
      next[i] = Math.min(DOUSE_MAX, (want > d ? d + (want - d) * creep : d) + mop);
    }
    const spent = douse;
    douse = next;
    next = spent;

    for (let i = 0; i < cols; i++) {
      // Neighbouring columns flicker together, so tongues of flame roll along the row.
      const flicker = 0.7 + 0.3 * Math.sin(t * 0.0041 * pace + i * 0.83 + phaseA) * Math.cos(t * 0.0016 * pace - i * 0.37 + phaseB);
      const ht = intensity * flicker * Math.max(0, 1 - douse[i]);
      heat[i] = ht;
      carry[i] += flameRate * crowd * ht * dt;
      while (carry[i] >= 1) {
        carry[i] -= 1;
        if (flameCount === maxFlames) continue;
        const j = flameCount++;
        const life = rand(36, 58);
        fX[j] = (i + Math.random()) * COL_W;
        fY[j] = h + rand(0, 14);
        fLife[j] = life;
        fAge[j] = 0;
        // They speed up as they climb, 1.2 times on average, and the visible tips stop short of the top.
        fVy[j] = -(tall * 1.1 * (0.3 + 0.7 * ht) * rand(0.55, 1.05)) / (life * 1.2);
        fVx[j] = rand(-0.25, 0.25);
        fR[j] = rand(18, 34) * size;
        fSeed[j] = rand(0, Math.PI * 2);
        fKind[j] = FLAME;
      }
    }

    for (let i = 0; i < flameCount; ) {
      if (fKind[i] === FLAME) {
        const dz = Math.min(1, douse[colOf(fX[i])]);
        fAge[i] += dt * (1 + DOUSED_BURN * dz);
        const a = fAge[i] / fLife[i];
        if (a >= 1) {
          if (Math.random() >= SMOKE_CHANCE + 0.5 * dz) {
            dropFlame(i);
            continue;
          }
          fKind[i] = SMOKE;
          fAge[i] = 0;
          fLife[i] = rand(30, 55);
          fR[i] *= 0.35;
          fVy[i] *= 1.3;
        } else {
          fX[i] += (fVx[i] + Math.sin(t * 0.0042 * pace + fY[i] * 0.017 + fX[i] * 0.011) * LICK * pace * a) * dt;
          fY[i] += fVy[i] * (0.8 + 0.8 * a) * dt;
        }
      } else {
        fAge[i] += dt;
        if (fAge[i] >= fLife[i]) {
          dropFlame(i);
          continue;
        }
        fX[i] += (fVx[i] + Math.sin(t * 0.002 + fSeed[i]) * 0.3 * pace) * dt;
        fY[i] += fVy[i] * (1 - 0.6 * (fAge[i] / fLife[i])) * dt;
      }
      i++;
    }

    // The extinguisher: in from the bottom right, tilted back as if shoved, upright while it sprays, then off the way it came.
    const u = Math.max(1.4, Math.min(h * 0.0029, w * 0.006, 2.9));
    const away = 1 - easeOut((t - EXT_IN_AT) / EXT_SLIDE_MS) + easeIn((t - EXT_OUT_AT) / EXT_SLIDE_MS);
    const spraying = t >= SPRAY_AT && t < SPRAY_AT + SPRAY_MS;
    const ex = w - 24 - 32 * u + away * (40 * u + 40);
    const ey = h + away * 125 * u;
    const tilt = quiet ? 0 : away * 0.22 + (spraying ? Math.sin(t * 0.05) * 0.012 : 0);
    const cos = Math.cos(tilt);
    const sin = Math.sin(tilt);
    const nbx = ex + (NOZZLE_X * cos - NOZZLE_Y * sin) * u;
    const nby = ey + (NOZZLE_X * sin + NOZZLE_Y * cos) * u;

    // Where the foam is aimed: at its own feet first, then sweeping left at an even pace so it piles evenly, ending on the far edge.
    const p = clamp01((t - SPRAY_AT) / SPRAY_MS);
    const along = (p - 0.04) / 0.81 + wobbleAmp * Math.sin(Math.PI * 2 * WOBBLES * p);
    const nearX = nbx - 14 * u;
    const aimX = nearX + (FAR_X - nearX) * clamp01(along);
    solve(nbx, nby, aimX);
    let aim = Math.atan2(svy, svx);
    solve(nbx + Math.cos(aim) * NOZZLE_LEN * u, nby + Math.sin(aim) * NOZZLE_LEN * u, aimX);
    aim = Math.atan2(svy, svx);
    const tipX = nbx + Math.cos(aim) * NOZZLE_LEN * u;
    const tipY = nby + Math.sin(aim) * NOZZLE_LEN * u;

    if (spraying) {
      foamCarry += foamRate * Math.max(0.5, Math.min(1.8, w / 900)) * dt;
      const foamSize = Math.max(0.7, Math.min(1.25, u / 2.4));
      while (foamCarry >= 1) {
        foamCarry -= 1;
        if (foamCount === maxFoam) continue;
        const j = foamCount++;
        fanStep = (fanStep + 0.618034) % 1;
        solve(tipX, tipY, aimX + (fanStep * 2 - 1 + rand(-0.15, 0.15)) * (10 + FAN * Math.abs(nearX - FAR_X)));
        // Launched part way through the frame, so the stream is even rather than in clumps.
        const lead = Math.random() * dt;
        bX[j] = tipX + rand(-1.5, 1.5) * u + svx * lead;
        bY[j] = tipY + rand(-1.5, 1.5) * u + svy * lead;
        bVx[j] = svx;
        bVy[j] = svy + rand(-0.4, 0.4);
        bR[j] = rand(7, 13) * foamSize;
        bAge[j] = lead;
        bLanded[j] = 0;
        bArt[j] = Math.random() < 0.3 ? 1 : 0;
      }
    }

    const pileMax = Math.max(24, h * 0.07);
    for (let i = 0; i < foamCount; ) {
      if (!bLanded[i]) {
        bAge[i] += dt;
        bVy[i] += gravity * dt;
        bX[i] += bVx[i] * dt;
        bY[i] += bVy[i] * dt;
        const c = colOf(bX[i]);
        const ground = h - pile[c];
        if (bVy[i] > 0 && bY[i] >= ground - bR[i] * 0.45) {
          bY[i] = ground - bR[i] * 0.45;
          bLanded[i] = 1;
          pile[c] = Math.min(pileMax, pile[c] + bR[i] * PILE_GAIN);
          douseAt(bX[i]);
        } else if (bX[i] < -60 || bX[i] > w + 60 || bY[i] > h + 60) {
          dropFoam(i);
          continue;
        }
      }
      i++;
    }
    const foamAlpha = 1 - smooth((t - FOAM_FADE_AT) / FOAM_FADE_MS);
    if (foamAlpha <= 0) foamCount = 0;

    // The glow: a soft warm wash per column that rises with the fire and goes where it goes out.
    const glowW = Math.max(150, COL_W * 7);
    const glowH = tall * 2.3 * (0.4 + 0.6 * intensity);
    for (let i = 0; i < cols; i += 2) {
      if (heat[i] < 0.01) continue;
      ctx.globalAlpha = GLOW_ALPHA * heat[i];
      ctx.drawImage(glowArt, (i + 0.5) * COL_W - glowW / 2, h - glowH / 2, glowW, glowH);
    }

    for (let i = 0; i < flameCount; i++) {
      if (fKind[i] !== SMOKE) continue;
      const a = fAge[i] / fLife[i];
      const r = fR[i] * (1 + 2.4 * a);
      ctx.globalAlpha = SMOKE_ALPHA * Math.min(1, a * 5) * (1 - a);
      ctx.drawImage(smokeArt, fX[i] - r, fY[i] - r, r * 2, r * 2);
    }

    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < flameCount; i++) {
      if (fKind[i] !== FLAME) continue;
      const a = fAge[i] / fLife[i];
      const r = fR[i] * (1 - 0.7 * a);
      ctx.globalAlpha = flameAlpha * Math.min(1, a * 10) * (a > 0.75 ? (1 - a) * 4 : 1);
      ctx.drawImage(flameArt[Math.min(STAGES - 1, Math.floor(a * STAGES))], fX[i] - r, fY[i] - r, r * 2, r * 2);
    }
    ctx.globalCompositeOperation = "source-over";

    ctx.globalAlpha = foamAlpha;
    const settle = 0.8 + 0.2 * foamAlpha;
    for (let i = 0; i < foamCount; i++) {
      if (!bLanded[i]) continue;
      const r = bR[i] * settle;
      ctx.drawImage(foamArt[bArt[i]], bX[i] - r, bY[i] - r * 0.9, r * 2, r * 1.8);
    }
    ctx.globalAlpha = 1;

    if (t > EXT_IN_AT && t < EXT_OUT_AT + EXT_SLIDE_MS) {
      paint ??= paints(ctx);
      ctx.save();
      ctx.translate(ex, ey);
      ctx.rotate(tilt);
      ctx.scale(u, u);
      drawExtinguisher(ctx, paint, body, aim - tilt, 1 / u, rim);
      ctx.restore();
    }

    // Foam in the air goes over the extinguisher, so it comes out of the nozzle rather than from behind it.
    ctx.globalAlpha = foamAlpha;
    for (let i = 0; i < foamCount; i++) {
      if (bLanded[i]) continue;
      const r = bR[i] * Math.min(1, 0.35 + bAge[i] / 14) * settle;
      ctx.drawImage(foamArt[bArt[i]], bX[i] - r, bY[i] - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;

    return t < END_MS && (t < FOAM_FADE_AT + FOAM_FADE_MS || flameCount > 0);
  });
}
